import { Directive, OnInit, ViewChild, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSort } from '@angular/material/sort';
import { SmsActions, SmsAdminFilterKey, SmsDto, SmsStatus, SmsTag } from '@workspace/data-types';
import {
  ActionButton,
  ActionMenu,
  BaseDataTableComponent,
  DataHeaderFilter,
  DataListColumn,
} from '@workspace/ng-aura';
import {
  smsActionDisplayTransform,
  smsStatusDisplayTransform,
  smsTagDisplayTransform,
} from '@workspace/ng-data-access';
import { Observable } from 'rxjs';

export type SmsColumnKey =
  'context' | 'to' | 'tag' | 'action' | 'status' | 'sendDate' | 'updatedAt' | 'createdAt';

@Directive()
export abstract class AbstractSmsDataTableCoreComponent<T extends SmsDto>
  extends BaseDataTableComponent<T, SmsColumnKey>
  implements OnInit
{
  override topMenu = signal<ActionMenu | null>(null);
  override columns = signal<DataListColumn<SmsColumnKey>[]>([
    { key: 'context', label: 'Contexte', display: true },
    { key: 'to', label: 'À', display: true },
    { key: 'tag', label: 'Tag', display: false },
    { key: 'action', label: 'Action', display: true },
    { key: 'status', label: 'Status', display: true },
    { key: 'sendDate', label: 'Envoyé le', display: false },
    { key: 'updatedAt', label: 'Date de modification', display: false },
    { key: 'createdAt', label: 'Date de création', display: false },
  ]);
  override topButtons = signal<ActionButton[]>([]);
  override itemActions = signal<ActionButton[]>([
    {
      label: 'Ouvrir',
      icon: 'open_in_new',
      onClick: (sms: SmsDto) => this.navigateToSms(sms),
      disabled: false,
      color: 'primary',
    },
  ]);
  override filters = signal<DataHeaderFilter<SmsAdminFilterKey>[]>([
    {
      label: 'Actions',
      column: SmsAdminFilterKey.Action,
    },
    {
      label: 'Tags',
      column: SmsAdminFilterKey.Tag,
    },
    {
      label: 'Status',
      column: SmsAdminFilterKey.Status,
    },
  ]);

  @ViewChild(MatSort) sort!: MatSort;

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const action = params.getAll(SmsAdminFilterKey.Action);
      const tag = params.getAll(SmsAdminFilterKey.Tag);
      const status = params.getAll(SmsAdminFilterKey.Status);
      this.filterChanged({
        changed: true,
        filter: {
          action: action.length ? action : [],
          tag: tag.length ? tag : [],
          status: status.length ? status : [],
        },
      });
    });
  }

  navigateToSms(sms: SmsDto): void {
    void this.router.navigate(['/sms', sms._id]);
  }

  abstract override getAll(): Observable<T[]>;
  abstract override countAll(): Observable<number>;
  override getDisplayForEntry(key: SmsAdminFilterKey, rawValue: string): string {
    switch (key) {
      case SmsAdminFilterKey.Action:
        return smsActionDisplayTransform(rawValue as SmsActions);
      case SmsAdminFilterKey.Tag:
        return smsTagDisplayTransform(rawValue as SmsTag);
      case SmsAdminFilterKey.Status:
        return smsStatusDisplayTransform(rawValue as SmsStatus);
      default:
        return key;
    }
  }
}
